import { type AlertColor } from '@mui/material';
import type { Components } from '@mui/material/styles/components';
import type { Theme } from '@mui/material/styles/createTheme';

const iconSm = { size: 18, offset: 6 };
const iconMd = { size: 21, offset: 10 };
const padding = { sm: 10, md: 16 };

export const MuiAlert: Components<Theme>['MuiAlert'] = {
  styleOverrides: {
    root: {
      borderRadius: '10px',
      '&.MuiAlert-size-sm': {
        padding: padding.sm,
        '& .MuiAlert-icon': {
          marginRight: iconSm.offset,
          fontSize: iconSm.size,
          maxWidth: iconSm.size,
          top: padding.sm,
          display: 'flex',
        },
        '& .MuiBox-root': {
          width: iconSm.size,
          height: iconSm.size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiAlert-action': {
          marginRight: 0,
          paddingLeft: '4px',
        },
        '& .MuiAlertTitle-root': {
          marginLeft: iconSm.size + iconSm.offset,
        },
      },
      '&.MuiAlert-size-md': {
        padding: padding.md,
        '& .MuiAlert-icon': {
          marginRight: iconMd.offset,
          fontSize: iconMd.size,
          maxWidth: iconMd.size,
          top: padding.md,
        },
        '& .MuiAlertTitle-root': {
          marginLeft: iconMd.size + iconMd.offset,
          marginRight: '10px',
        },
      },
      '& .MuiButton-contained, & .MuiButton-outlined': {
        marginTop: '10px',
        height: '41px',
        width: '100%',
      },
      '& .MuiButton-contained': {
        '&.MuiButton-colorPrimary': {
          backgroundColor: '#EEF2FD',
          color: '#6948EB',
          '&:hover': {
            backgroundColor: '#D4DFF9',
          },
        },
        '&.MuiButton-colorSuccess': {
          backgroundColor: '#DFF1E5',
          color: '#3CA73B',
          '&:hover': {
            backgroundColor: '#AFDDBF',
          },
        },
        '&.MuiButton-colorError': {
          backgroundColor: '#FCEAEA',
          color: '#E02727',
          '&:hover': {
            backgroundColor: '#F7CBCB',
          },
        },
      },
    },
    message: () => ({
      paddingTop: 0,
      paddingBottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      overflow: 'visible',
      width: '100%',
    }),
    icon: () => ({
      padding: 0,
      height: '21px',
      display: 'flex',
      alignItems: 'center',
      width: 'fit-content',
      fontSize: '14px',
      '& svg.MuiSvgIcon-root': {
        width: '1em',
        height: '1em',
        fontSize: '14px',
      },
    }),
    action: () => ({
      paddingTop: 0,
      position: 'absolute',
      right: '16px',
    }),
    standard: {
      position: 'relative',
      '& .MuiAlert-icon': {
        position: 'absolute',
      },
    },
  },
  variants: [
    {
      props: {
        severity: 'error',
      },
      style: ({ theme }) => ({
        backgroundColor: '#FEF7F7',
        color: theme.palette.error.contrastText,
        border: `1px solid ${theme.palette.error.contrastText}`,
        '& .MuiSvgIcon-root': {
          color: theme.palette.error.contrastText,
        },
        '& .MuiTypography-subtitle2:not(.details)': {
          color: theme.palette.error.contrastText,
        },
      }),
    },
    {
      props: {
        severity: 'warning',
      },
      style: {
        backgroundColor: '#FDF9F5',
        color: '#4E451A',
        border: '1px solid #CF6300',
        '& .MuiSvgIcon-root': {
          color: '#CF6300',
        },
      },
    },
    {
      props: {
        severity: 'success',
      },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        border: `1px solid ${theme.palette.success.contrastText}`,
        '& .MuiSvgIcon-root:not(.MuiAlert-close)': {
          color: '#fff',
        },
      }),
    },
    {
      props: {
        severity: 'info',
      },
      style: {
        backgroundColor: '#F8FAFE',
        color: '#939DB8',
        border: '1px solid #6948EB',
        '& .MuiAlert-close': {
          color: '#6948EB',
        },
      },
    },
    {
      props: {
        severity: 'custom-info' as AlertColor,
      },
      style: ({ theme }) => ({
        backgroundColor: '#F8FAFE',
        color: '#071B3A',
        border: `1px solid #F8FAFE`,
        '& .MuiTypography-subtitle2:not(.details)': {
          color: theme.palette.error.contrastText,
        },
      }),
    },
  ],
};
